import type { NavProps } from 'src/components/navbar';

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import useTheme from '@mui/system/useTheme';

import { getFilteredNavData } from 'src/utils/utils';
import { UserRole, Permission } from 'src/utils/enumerations';

import { useAuthContext } from 'src/auth';
import NavBarIcon from 'src/pages/components/NavBarIcon';

import CustomNavSectionMini from 'src/components/navbar/mini/CustomNavSectionMini';

import { HEADER } from '../layoutConfig';

const SALES_RELATED_ROLES = [UserRole.Sales, UserRole.SuperUser, UserRole.SalesManager];

const navData: NavProps['data'] = [
  {
    subheader: '',
    items: [
      {
        id: 'sales',
        title: 'Sales',
        path: '/sales',
        icon: NavBarIcon('ic_sales'),
        roles: SALES_RELATED_ROLES,
        children: [
          {
            id: 'sales-analytics',
            title: 'Dashboard',
            path: '/sales/analytics',
            icon: NavBarIcon('ic_dashboard'),
            permissions: [Permission.SMView, Permission.SearchSMAll],
          },
          // Any user with Sales Roles can access the Search and My Uploads sections by default.
          {
            id: 'sales-search',
            title: 'Search',
            path: '/sales/search',
            icon: NavBarIcon('ic_search'),
          },
          {
            id: 'sales-home',
            title: 'My Uploads',
            path: '/sales',
            icon: NavBarIcon('ic_upload'),
          },
        ],
      },
      {
        id: 'compliance',
        title: 'Compliance',
        path: '/compliance',
        icon: NavBarIcon('ic_compliance'),
        roles: [UserRole.Compliance],
      },
      {
        id: 'servicing',
        title: 'Servicing',
        path: '/servicing',
        icon: NavBarIcon('ic_servicing'),
        roles: [UserRole.Servicing],
      },
    ],
  },
];

const MiniSideNav = () => {
  const theme = useTheme();
  const { user, hasPermissions, hasRoles } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [filteredNavData, setFilteredNavData] = useState<NavProps['data']>([]);

  /**
   * Filters the navigation data based on the user's roles and sets the filtered data.
   * This effect runs whenever the user object changes.
   */
  useEffect(() => {
    const userRoles = user?.roles ?? [];
    if (userRoles.length > 0) {
      setFilteredNavData(getFilteredNavData(navData, userRoles));
    }
  }, [user]);

  /**
   * Redirects the user to a default landing page on initial application load.
   * If the user lands on the root path and has navigation items available,
   * it navigates them to a specific page based on permissions, or to the first available page.
   */
  useEffect(() => {
    if (
      (!location.pathname || location.pathname === '/') &&
      filteredNavData.length > 0 &&
      filteredNavData[0].items.length > 0
    ) {
      if (hasPermissions([Permission.SMView, Permission.SearchSMAll])) {
        navigate('/sales/analytics');
      } else if (hasRoles(SALES_RELATED_ROLES)) {
        navigate('/sales/search');
      } else {
        navigate(filteredNavData[0].items[0].path);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, filteredNavData]);

  return (
    <Box
      component="nav"
      sx={{
        minHeight: `calc(100vh - ${HEADER.H_DESKTOP}px)`,
        backgroundColor: theme.palette.common.white,
        borderRight: `1px dashed ${theme.palette.divider}`,
        minWidth: 86,
        top: 0,
        position: 'sticky',
        pt: 2,
        zIndex: 1,
      }}
    >
      <CustomNavSectionMini data={filteredNavData} />
    </Box>
  );
};
export default MiniSideNav;
